import { FunctionComponent } from 'react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import Tooltip from '@mui/material/Tooltip';
import { useFormContext, useWatch } from 'react-hook-form';
import { FaTimes } from 'react-icons/fa';
import {
  LuAlignCenter,
  LuAlignLeft,
  LuAlignRight,
} from 'react-icons/lu';
import { RxPadding } from 'react-icons/rx';
import { zIndexes } from '../../../../../../styling/zIndexes';
import { cn } from '../../../../../../utilities/cn';
import type { FormatOptions } from '../../types/KidPdfGeneratorV2.types';
import LinkedSpacingControl from './LinkedSpacingControl';

export const hasTextBlockCustomChanges = (
  formatOptions: FormatOptions | undefined,
): boolean => {
  if (!formatOptions) return false;
  const opts = formatOptions as Record<string, unknown>;

  const hasMargin =
    (opts.margin && opts.margin !== 0) ||
    (opts.marginTop && opts.marginTop !== 0) ||
    (opts.marginRight && opts.marginRight !== 0) ||
    (opts.marginBottom && opts.marginBottom !== 0) ||
    (opts.marginLeft && opts.marginLeft !== 0);

  const hasPadding =
    (opts.padding && opts.padding !== 0) ||
    (opts.paddingTop && opts.paddingTop !== 0) ||
    (opts.paddingRight && opts.paddingRight !== 0) ||
    (opts.paddingBottom && opts.paddingBottom !== 0) ||
    (opts.paddingLeft && opts.paddingLeft !== 0);

  const hasBorder =
    (opts.borderWidth && opts.borderWidth !== 0) ||
    (opts.borderRadius && opts.borderRadius !== 0) ||
    (opts.borderColor && opts.borderColor !== '#000000');

  const hasBackground =
    opts.backgroundColor && opts.backgroundColor !== '#ffffff';

  return Boolean(hasMargin || hasPadding || hasBorder || hasBackground);
};

const SPACING_APPEARANCE_DEFAULTS: Record<string, unknown> = {
  margin: 0,
  marginTop: 0,
  marginRight: 0,
  marginBottom: 0,
  marginLeft: 0,
  padding: 0,
  paddingTop: 0,
  paddingRight: 0,
  paddingBottom: 0,
  paddingLeft: 0,
  borderWidth: 0,
  borderRadius: 0,
  borderColor: '#000000',
  backgroundColor: '#ffffff',
};

interface TextBlockStyleControlsProps {
  formatOptionsPath: string;
  idPrefix?: string | number;
  disabled?: boolean;
  compact?: boolean;
}

const getToolbarButtonClass = (
  compact: boolean,
  active = false,
  isDisabled = false,
): string =>
  cn(
    'border transition-colors focus:outline-none',
    compact
      ? 'flex h-7 w-7 shrink-0 items-center justify-center rounded-md'
      : 'rounded p-2',
    isDisabled
      ? 'cursor-not-allowed border-gray-200 text-gray-400'
      : active
        ? compact
          ? 'border-blue-400 bg-blue-50 text-blue-700'
          : 'border-blue-500 bg-blue-50 text-blue-700'
        : 'border-gray-200 text-gray-600 hover:border-blue-500 hover:bg-gray-100 hover:text-blue-600',
  );

const TextBlockStyleControls: FunctionComponent<TextBlockStyleControlsProps> = ({
  formatOptionsPath,
  idPrefix = 'text-block',
  disabled = false,
  compact = false,
}) => {
  const { control, setValue } = useFormContext();
  const formatOptions = useWatch({
    name: formatOptionsPath,
    control,
  }) as FormatOptions | undefined;

  const handleStyleChange = (
    key: keyof FormatOptions,
    value: string | number | boolean,
  ) => {
    const currentStyles = (formatOptions || {}) as Record<string, unknown>;
    const updatedStyles: Record<string, unknown> = {
      ...currentStyles,
      [key]:
        typeof value === 'string' && !isNaN(Number(value))
          ? Number(value)
          : value,
    };
    setValue(formatOptionsPath, updatedStyles as FormatOptions);
  };

  const resetSpacingAndAppearance = () => {
    const currentOptions = (formatOptions || {}) as Record<string, unknown>;
    const updatedOptions: Record<string, unknown> = { ...currentOptions };

    Object.keys(SPACING_APPEARANCE_DEFAULTS).forEach((key) => {
      delete updatedOptions[key];
    });

    const hasOtherOptions = Object.keys(updatedOptions).some(
      (key) => !Object.keys(SPACING_APPEARANCE_DEFAULTS).includes(key),
    );

    setValue(
      formatOptionsPath,
      hasOtherOptions ? (updatedOptions as FormatOptions) : undefined,
    );
  };

  const iconSize = compact ? 14 : 18;

  const controls = (
    <>
      <Popover className="relative">
        <Tooltip
          title="Spacing & Appearance"
          arrow
          placement="bottom"
          slotProps={{
            arrow: {
              className: 'text-mainblue',
            },
          }}
        >
          <span className="inline-flex">
            <PopoverButton
              disabled={disabled}
              className={cn(
                getToolbarButtonClass(
                  compact,
                  hasTextBlockCustomChanges(formatOptions),
                  disabled,
                ),
                'relative',
              )}
              aria-label="Spacing & appearance"
            >
              <RxPadding size={iconSize} />
              {hasTextBlockCustomChanges(formatOptions) && (
                <span
                  className={cn(
                    'absolute rounded-full bg-blue-500 ring-2 ring-white',
                    compact ? '-right-0.5 -top-0.5 h-2 w-2' : '-right-1 -top-1 h-2.5 w-2.5',
                  )}
                />
              )}
            </PopoverButton>
          </span>
        </Tooltip>
        <PopoverPanel
          anchor={{ to: 'bottom start', gap: 8, padding: 16 }}
          style={{ zIndex: zIndexes.floatingPanel }}
          className="w-[400px] rounded-md border border-gray-200 bg-white p-3 text-xs shadow-xl [--anchor-max-height:none]"
        >
          <div className="mb-2 flex items-center justify-between">
            <div className="font-semibold text-gray-600">
              Spacing & Appearance
            </div>
            {hasTextBlockCustomChanges(formatOptions) && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  resetSpacingAndAppearance();
                }}
                className="flex items-center gap-1.5 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-600 transition-colors hover:border-red-300 hover:bg-red-100 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                aria-label="Reset spacing and appearance"
                title="Reset all spacing and appearance"
              >
                <FaTimes size={12} />
                <span>Clear all</span>
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <LinkedSpacingControl
              title="Margin"
              valueRoot={Number((formatOptions as Record<string, unknown>)?.margin ?? 0)}
              valueTop={Number(
                (formatOptions as Record<string, unknown>)?.marginTop ?? 0,
              )}
              valueRight={Number(
                (formatOptions as Record<string, unknown>)?.marginRight ?? 0,
              )}
              valueBottom={Number(
                (formatOptions as Record<string, unknown>)?.marginBottom ?? 0,
              )}
              valueLeft={Number(
                (formatOptions as Record<string, unknown>)?.marginLeft ?? 0,
              )}
              onRootChange={(v) => handleStyleChange('margin', v)}
              onTopChange={(v) => handleStyleChange('marginTop', v)}
              onRightChange={(v) => handleStyleChange('marginRight', v)}
              onBottomChange={(v) => handleStyleChange('marginBottom', v)}
              onLeftChange={(v) => handleStyleChange('marginLeft', v)}
            />
            <LinkedSpacingControl
              title="Padding"
              valueRoot={Number((formatOptions as Record<string, unknown>)?.padding ?? 0)}
              valueTop={Number(
                (formatOptions as Record<string, unknown>)?.paddingTop ?? 0,
              )}
              valueRight={Number(
                (formatOptions as Record<string, unknown>)?.paddingRight ?? 0,
              )}
              valueBottom={Number(
                (formatOptions as Record<string, unknown>)?.paddingBottom ?? 0,
              )}
              valueLeft={Number(
                (formatOptions as Record<string, unknown>)?.paddingLeft ?? 0,
              )}
              onRootChange={(v) => handleStyleChange('padding', v)}
              onTopChange={(v) => handleStyleChange('paddingTop', v)}
              onRightChange={(v) => handleStyleChange('paddingRight', v)}
              onBottomChange={(v) => handleStyleChange('paddingBottom', v)}
              onLeftChange={(v) => handleStyleChange('paddingLeft', v)}
            />
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 border-t border-gray-200 pt-3">
            <div className="col-span-1 flex items-center justify-between gap-2">
              <span className="text-gray-600">Border</span>
              <input
                type="number"
                className="ml-2 w-20 rounded border border-gray-300 p-1 text-[12px]"
                value={Number(
                  (formatOptions as Record<string, unknown>)?.borderWidth ?? 0,
                )}
                onChange={(e) =>
                  handleStyleChange(
                    'borderWidth',
                    Number(e.target.value || 0),
                  )
                }
              />
            </div>
            <div className="col-span-1 flex items-center justify-between gap-2">
              <span className="text-gray-600">Radius</span>
              <input
                type="number"
                className="ml-2 w-20 rounded border border-gray-300 p-1 text-[12px]"
                value={Number(
                  (formatOptions as Record<string, unknown>)?.borderRadius ?? 0,
                )}
                onChange={(e) =>
                  handleStyleChange(
                    'borderRadius',
                    Number(e.target.value || 0),
                  )
                }
              />
            </div>
            <div className="col-span-1 flex items-center justify-between gap-2">
              <span className="text-gray-600">Border Color</span>
              <input
                type="color"
                className="h-6 w-6 cursor-pointer rounded border border-gray-300"
                value={
                  ((formatOptions as Record<string, unknown>)?.borderColor as string) ??
                  '#000000'
                }
                onChange={(e) =>
                  handleStyleChange('borderColor', e.target.value)
                }
              />
            </div>
            <div className="col-span-1 flex items-center justify-between gap-2">
              <span className="text-gray-600">Background</span>
              <input
                type="color"
                className="h-6 w-6 cursor-pointer rounded border border-gray-300"
                value={
                  ((formatOptions as Record<string, unknown>)
                    ?.backgroundColor as string) ?? '#ffffff'
                }
                onChange={(e) =>
                  handleStyleChange('backgroundColor', e.target.value)
                }
              />
            </div>
          </div>
        </PopoverPanel>
      </Popover>

      <div className={cn('flex items-center', compact ? 'gap-0.5' : 'gap-1')}>
        <Tooltip
          title="Align left"
          arrow
          placement="bottom"
          slotProps={{
            arrow: {
              className: 'text-mainblue',
            },
          }}
        >
          <span className="inline-flex">
            <button
              type="button"
              disabled={disabled}
              onClick={() => handleStyleChange('textAlign', 'left')}
              className={getToolbarButtonClass(
                compact,
                formatOptions?.textAlign === 'left',
                disabled,
              )}
              aria-label="Align left"
            >
              <LuAlignLeft size={iconSize} />
            </button>
          </span>
        </Tooltip>
        <Tooltip
          title="Align center"
          arrow
          placement="bottom"
          slotProps={{
            arrow: {
              className: 'text-mainblue',
            },
          }}
        >
          <span className="inline-flex">
            <button
              type="button"
              disabled={disabled}
              onClick={() => handleStyleChange('textAlign', 'center')}
              className={getToolbarButtonClass(
                compact,
                formatOptions?.textAlign === 'center',
                disabled,
              )}
              aria-label="Align center"
            >
              <LuAlignCenter size={iconSize} />
            </button>
          </span>
        </Tooltip>
        <Tooltip
          title="Align right"
          arrow
          placement="bottom"
          slotProps={{
            arrow: {
              className: 'text-mainblue',
            },
          }}
        >
          <span className="inline-flex">
            <button
              type="button"
              disabled={disabled}
              onClick={() => handleStyleChange('textAlign', 'right')}
              className={getToolbarButtonClass(
                compact,
                formatOptions?.textAlign === 'right',
                disabled,
              )}
              aria-label="Align right"
            >
              <LuAlignRight size={iconSize} />
            </button>
          </span>
        </Tooltip>
      </div>

      <div
        className={cn(
          'flex items-center',
          compact ? 'gap-1.5 border-l border-gray-200 pl-2' : 'gap-1 px-1',
        )}
      >
        <label
          className={cn(
            'font-medium text-gray-600',
            compact ? 'text-xs' : 'text-sm',
          )}
          htmlFor={`fontSize-${idPrefix}`}
        >
          Size
        </label>
        <input
          id={`fontSize-${idPrefix}`}
          type="number"
          min={8}
          max={72}
          step={1}
          value={Number(formatOptions?.fontSize ?? 12)}
          disabled={disabled}
          onChange={(e) => {
            handleStyleChange('fontSize', Number(e.target.value || 12));
          }}
          className={cn(
            'rounded-md border border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500',
            compact
              ? 'h-7 w-11 px-1.5 text-xs'
              : 'h-8 w-14 p-1 text-sm',
            disabled && 'cursor-not-allowed bg-gray-50 text-gray-400',
          )}
        />
      </div>
    </>
  );

  if (compact) {
    return (
      <div className="flex flex-wrap items-center gap-1.5">{controls}</div>
    );
  }

  return controls;
};

export default TextBlockStyleControls;
